/**
 * Rendering helpers for archive values.
 *
 * Dates are formatted in UTC on purpose: the API returns UTC instants and a
 * published date that shifts by a day in the reader's timezone would misstate
 * the record.
 */

/** `null` impressions/amounts mean "not yet computed" — never render them as 0. */
export const NOT_YET_REPORTED = 'Not yet reported'

/** Used for fields the API returned as null/empty where no legal claim is implied. */
export const NOT_AVAILABLE = 'Not provided'

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
})

function parse(value: string | null | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatDate(value: string | null | undefined): string {
  const date = parse(value)
  return date ? dateFormatter.format(date) : NOT_AVAILABLE
}

export function formatDateTime(value: string | null | undefined): string {
  const date = parse(value)
  return date ? `${dateTimeFormatter.format(date)} UTC` : NOT_AVAILABLE
}

/** Machine-readable value for <time dateTime="…">. */
export function isoDateTime(value: string | null | undefined): string | undefined {
  return parse(value)?.toISOString()
}

const numberFormatter = new Intl.NumberFormat('en-US')

/**
 * Amounts come back with sub-cent precision (e.g. 1832.8739). Rounding to two
 * decimals would restate a reported legal figure, so the exact value is shown —
 * whole cents still render as $1,250.50.
 */
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

/** null → "Not yet reported". A zero would assert the ad ran and delivered nothing. */
export function formatImpressions(value: number | null): string {
  return value === null || value === undefined ? NOT_YET_REPORTED : numberFormatter.format(value)
}

/** null → "Not yet reported". A zero would assert the ad ran and cost nothing. */
export function formatAmount(value: number | null): string {
  return value === null || value === undefined ? NOT_YET_REPORTED : currencyFormatter.format(value)
}

/** Free-text fields that may come back empty. */
export function textOrFallback(value: string | null | undefined): string {
  const trimmed = value?.trim()
  return trimmed ? trimmed : NOT_AVAILABLE
}

/**
 * Enum labels. The API documents these as OPEN sets, so an unrecognised value is
 * humanised rather than dropped or treated as an error (docs/api.md §9).
 */
function humanise(value: string): string {
  const words = value.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : NOT_AVAILABLE
}

function labelFrom(labels: Record<string, string>, value: string | undefined): string {
  if (!value) return NOT_AVAILABLE
  return labels[value.toLowerCase()] ?? humanise(value)
}

/** ad_type: banner | video | native | audio */
export function adTypeLabel(value: string | undefined): string {
  return labelFrom({ banner: 'Banner', video: 'Video', native: 'Native', audio: 'Audio' }, value)
}

/** position: support | oppose */
export function positionLabel(value: string | undefined): string {
  return labelFrom({ support: 'Supports', oppose: 'Opposes' }, value)
}

/** subject_type: candidate | ballot_measure — also decides how subject_name is labelled. */
export function subjectTypeLabel(value: string | undefined): string {
  return labelFrom({ candidate: 'Candidate', ballot_measure: 'Ballot measure' }, value)
}

/** The heading for subject_name, which is a person for a candidate and a measure otherwise. */
export function subjectNameLabel(subjectType: string | undefined): string {
  if (subjectType?.toLowerCase() === 'candidate') return 'Candidate'
  if (subjectType?.toLowerCase() === 'ballot_measure') return 'Ballot measure'
  return 'Subject'
}

/**
 * True for a real calendar date in the `YYYY-MM-DD` form the API requires.
 *
 * Needed because `<input type="date">` cannot be relied on to produce one:
 * a browser without native support (Safari on macOS before 14.1) renders it as a
 * plain text field where the user can type anything, and a `?from=` value can
 * always be hand-edited in the URL. The API answers a malformed date with
 * `400 from must be a date in YYYY-MM-DD format`, and docs/api.md §6 asks us to
 * catch that client-side instead of spending a round trip on it.
 *
 * Rejects impossible dates (`2026-02-31`) as well as malformed ones.
 */
export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false

  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  )
}
